import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Select,
  Stack,
  TextField,
  Typography,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import JarCard from "../components/Jars/JarCard";
import { Jar, JarDetailsDialog } from "../components/Jars/JarDetailsDialog";
import { api, ApiJar, ApiJarActivity, ApiJarInvite } from "../services/api";

/** Convert API jar shape into the component Jar type */
function toJar(j: ApiJar): Jar {
  return {
    id: j.id,
    name: j.name,
    description: j.description,
    targetAmountCents: j.targetAmountCents,
    currentAmountCents: j.currentAmountCents,
    targetDate: j.targetDate,
    currency: j.currency,
    icon: j.icon,
    color: j.color,
    ownerId: j.ownerId,
    expenseCount: j.expenseCount,
    totalExpenseCents: j.totalExpenseCents,
    updatedAt: j.updatedAt,
    owner: j.owner,
    members: j.members,
  };
}

const ICON_OPTIONS = ["💰", "💍", "✈️", "🏠", "🎓", "🚗", "🏖️", "🎯", "🛡️", "🎉"];
const COLOR_OPTIONS = ["#c82f27", "#007AFF", "#34C759", "#AF52DE", "#FF9500", "#FF2D55", "#5856D6", "#00C7BE"];
type JarVisibilityFilter = 'all' | 'shared' | 'personal';
type JarSortOption = 'updated' | 'name' | 'saved' | 'progress';

function formatCurrency(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTimestamp(value: string) {
  const date = new Date(value);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) {
    const diffMinutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function getActivitySummary(activity: ApiJarActivity) {
  const actor = activity.actorName || activity.actorEmail || 'Someone';
  const subject = activity.subjectName || activity.subjectEmail;

  switch (activity.type) {
    case 'contribution':
      return {
        title: `${actor} contributed to ${activity.jarName}`,
        subtitle: activity.description === 'Contribution' ? formatTimestamp(activity.createdAt) : `${activity.description} · ${formatTimestamp(activity.createdAt)}`,
      };
    case 'expense':
      return {
        title: `${actor} spent from ${activity.jarName}`,
        subtitle: `${activity.description} · ${formatTimestamp(activity.createdAt)}`,
      };
    case 'invite_sent':
      return {
        title: `${actor} invited ${subject ?? 'a collaborator'} to ${activity.jarName}`,
        subtitle: `${activity.description === 'EDITOR' ? 'Editor' : 'Viewer'} invite · ${formatTimestamp(activity.createdAt)}`,
      };
    case 'invite_accepted':
      return {
        title: `${subject ?? actor} joined ${activity.jarName}`,
        subtitle: `Invite accepted · ${formatTimestamp(activity.createdAt)}`,
      };
    case 'invite_declined':
      return {
        title: `${subject ?? actor} declined an invite to ${activity.jarName}`,
        subtitle: formatTimestamp(activity.createdAt),
      };
    case 'jar_created':
    default:
      return {
        title: `${actor} created ${activity.jarName}`,
        subtitle: formatTimestamp(activity.createdAt),
      };
  }
}

function getActivityInitial(activity: ApiJarActivity) {
  if (activity.type === 'contribution') return '$';
  if (activity.type === 'expense') return '−';
  if (activity.type.startsWith('invite')) return '✉';
  return 'J';
}

export default function SharedJarsPage({
  setHeaderActions,
}: {
  setHeaderActions: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}) {
  const [jars, setJars] = useState<Jar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invites, setInvites] = useState<ApiJarInvite[]>([]);
  const [activity, setActivity] = useState<ApiJarActivity[]>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);
  const [jarFilter, setJarFilter] = useState<JarVisibilityFilter>('all');
  const [jarSort, setJarSort] = useState<JarSortOption>('updated');

  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedJar, setSelectedJar] = useState<Jar | null>(null);
  const [detailsInitialTab, setDetailsInitialTab] = useState<number | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedJarId, setSelectedJarId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [newJar, setNewJar] = useState({
    name: "",
    description: "",
    targetAmount: "",
    targetDate: "",
    icon: "💰",
    color: "#34C759",
  });
  const [formErrors, setFormErrors] = useState<{ name?: string; targetAmount?: string }>({});

  /* ── Fetch jars ──────────────────────────────────────── */
  const refreshSidebarData = useCallback(async () => {
    const [invitesRes, activityRes] = await Promise.all([
      api.getJarInvites(),
      api.getJarActivity(8),
    ]);
    setInvites(invitesRes.invites);
    setActivity(activityRes.activity);
  }, []);

  const fetchJars = useCallback(async () => {
    try {
      setError(null);
      const [jarsRes] = await Promise.all([
        api.getJars(),
        refreshSidebarData(),
      ]);
      setJars(jarsRes.jars.map(toJar));
    } catch (err: any) {
      setError(err.message ?? "Failed to load jars");
    } finally {
      setLoading(false);
    }
  }, [refreshSidebarData]);

  useEffect(() => { fetchJars(); }, [fetchJars]);

  /* ── Header action ───────────────────────────────────── */
  useEffect(() => {
    setHeaderActions(
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          variant="outlined"
          startIcon={<MailOutlineIcon />}
          onClick={() => setInviteDialogOpen(true)}
        >
          <Badge badgeContent={invites.length} color="error" invisible={invites.length === 0} sx={{ '& .MuiBadge-badge': { right: -10, top: 8 } }}>
            <Box component="span" sx={{ pr: invites.length > 0 ? 1.5 : 0 }}>
              Invites{invites.length > 0 ? ` (${invites.length})` : ''}
            </Box>
          </Badge>
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Create New Jar
        </Button>
      </Stack>
    );
    return () => setHeaderActions(null);
  }, [invites.length, setHeaderActions]);

  /* ── Create jar ──────────────────────────────────────── */
  const handleAddJar = async () => {
    const errors: { name?: string; targetAmount?: string } = {};
    if (!newJar.name.trim()) {
      errors.name = "Jar name is required";
    }
    const targetDollars = parseFloat(newJar.targetAmount);
    if (newJar.targetAmount && (isNaN(targetDollars) || targetDollars < 0)) {
      errors.targetAmount = "Enter a valid amount";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      const amountCents = newJar.targetAmount ? Math.round(targetDollars * 100) : 0;
      await api.createJar({
        name: newJar.name.trim(),
        description: newJar.description || undefined,
        targetAmountCents: amountCents,
        currentAmountCents: 0,
        targetDate: newJar.targetDate || undefined,
        currency: "USD",
        icon: newJar.icon,
        color: newJar.color,
      });
      await fetchJars();
      setNewJar({ name: "", description: "", targetAmount: "", targetDate: "", icon: "💰", color: "#34C759" });
      setFormErrors({});
      setOpenDialog(false);
    } catch (err: any) {
      setError(err.message ?? "Failed to create jar");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete jar ──────────────────────────────────────── */
  const handleDeleteJar = async (id: string) => {
    try {
      await api.deleteJar(id);
      await fetchJars();
    } catch (err: any) {
      setError(err.message ?? "Failed to delete jar");
    }
    setAnchorEl(null);
    setSelectedJarId(null);
  };

  /* ── Menu / click handlers ───────────────────────────── */
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, jarId: string) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedJarId(jarId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedJarId(null);
  };

  const handleJarClick = (jar: Jar) => {
    setSelectedJar(jar);
    setDetailsInitialTab(undefined);
    setOpenDetailsDialog(true);
  };

  /* ── Derived values ──────────────────────────────────── */
  const totalSavedCents = useMemo(
    () => jars.reduce((sum, jar) => sum + jar.currentAmountCents, 0),
    [jars]
  );
  const totalGoalCents = useMemo(
    () => jars.reduce((sum, jar) => sum + jar.targetAmountCents, 0),
    [jars]
  );

  const statTiles = useMemo(() => [
    {
      label: 'Total contributed to jars',
      value: formatCurrency(totalSavedCents),
      subtitle: `${jars.filter((jar) => jar.currentAmountCents > 0).length} funded jars`,
      icon: <SavingsOutlinedIcon color="action" />,
    },
    {
      label: 'Total jar goals',
      value: formatCurrency(totalGoalCents),
      subtitle: `${jars.filter((jar) => jar.targetAmountCents > 0).length} jars with goals`,
      icon: <FlagOutlinedIcon color="action" />,
    },
    {
      label: 'Total jars',
      value: `${jars.length}`,
      subtitle: `${jars.filter((jar) => jar.members.length > 1).length} shared jars`,
      icon: <Inventory2OutlinedIcon color="action" />,
    },
  ], [jars, totalGoalCents, totalSavedCents]);

  const visibleJars = useMemo(() => {
    const filtered = jars.filter((jar) => {
      if (jarFilter === 'shared') return jar.members.length > 1;
      if (jarFilter === 'personal') return jar.members.length <= 1;
      return true;
    });

    const sorted = [...filtered].sort((left, right) => {
      switch (jarSort) {
        case 'name':
          return left.name.localeCompare(right.name);
        case 'saved':
          return right.currentAmountCents - left.currentAmountCents;
        case 'progress': {
          const leftProgress = left.targetAmountCents > 0 ? left.currentAmountCents / left.targetAmountCents : 0;
          const rightProgress = right.targetAmountCents > 0 ? right.currentAmountCents / right.targetAmountCents : 0;
          return rightProgress - leftProgress;
        }
        case 'updated':
        default:
          return new Date(right.updatedAt ?? 0).getTime() - new Date(left.updatedAt ?? 0).getTime();
      }
    });

    return sorted;
  }, [jarFilter, jarSort, jars]);

  const handleInviteResponse = async (inviteId: string, action: 'accept' | 'decline') => {
    try {
      setInviteActionId(inviteId);
      await api.respondToJarInvite(inviteId, action);
      await fetchJars();
    } catch (err: any) {
      setError(err.message ?? `Failed to ${action} invite`);
    } finally {
      setInviteActionId(null);
    }
  };

  /* ── Render ──────────────────────────────────────────── */
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8, width: '100%', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        px: { xs: 1, sm: 2, md: 3 },
        pb: { xs: 2, md: 3 },
      }}
    >
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2, mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3, mb: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
        {statTiles.map((tile) => (
          <Card key={tile.label} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {tile.label}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {tile.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {tile.subtitle}
                  </Typography>
                </Box>
                <Box sx={{ color: 'text.secondary' }}>
                  {tile.icon}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 320px' }, gap: 2, alignItems: 'start' }}>
        <Box>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'stretch', lg: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Active Jars
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="flex-end"
              useFlexGap
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {([
                  { value: 'all', label: 'All' },
                  { value: 'shared', label: 'Shared' },
                  { value: 'personal', label: 'Personal' },
                ] as Array<{ value: JarVisibilityFilter; label: string }>).map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    color={jarFilter === option.value ? 'primary' : 'default'}
                    variant={jarFilter === option.value ? 'filled' : 'outlined'}
                    onClick={() => setJarFilter(option.value)}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>

              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                <InputLabel id="jar-sort-label">Sort by</InputLabel>
                <Select
                  labelId="jar-sort-label"
                  value={jarSort}
                  label="Sort by"
                  onChange={(e) => setJarSort(e.target.value as JarSortOption)}
                >
                  <MenuItem value="updated">Recently updated</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="saved">Most saved</MenuItem>
                  <MenuItem value="progress">Goal progress</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {visibleJars.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 4, gridColumn: "1 / -1" }}>
                {jars.length === 0 ? 'No jars yet. Create one to start saving!' : 'No jars match the selected filters.'}
              </Typography>
            ) : (
              visibleJars.map((jar) => (
                <JarCard key={jar.id} jar={jar} onMenuOpen={handleMenuOpen} onClick={handleJarClick} />
              ))
            )}
          </Box>
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Recent Activity
              </Typography>
              <Chip label={`View ${activity.length}`} size="small" variant="outlined" />
            </Stack>
            <Divider />
            {activity.length === 0 ? (
              <Typography color="text.secondary" sx={{ px: 3, py: 4 }}>
                No recent jar activity yet.
              </Typography>
            ) : (
              <List disablePadding>
                {activity.map((item, index) => {
                  const summary = getActivitySummary(item);
                  return (
                    <React.Fragment key={item.id}>
                      <ListItem alignItems="flex-start" sx={{ px: 3, py: 2 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: item.type === 'expense' ? 'error.light' : item.type === 'contribution' ? 'success.light' : 'primary.light', color: item.type === 'expense' ? 'error.dark' : item.type === 'contribution' ? 'success.dark' : 'primary.dark' }}>
                            {getActivityInitial(item)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{summary.title}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">{summary.subtitle}</Typography>}
                        />
                        {item.amountCents != null ? (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: item.amountCents >= 0 ? 'success.main' : 'text.primary', whiteSpace: 'nowrap', ml: 1 }}>
                            {item.amountCents >= 0 ? '+' : '-'}{formatCurrency(Math.abs(item.amountCents)).replace('$', '$')}
                          </Typography>
                        ) : null}
                      </ListItem>
                      {index < activity.length - 1 ? <Divider component="li" /> : null}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pending Invites</DialogTitle>
        <DialogContent dividers>
          {invites.length === 0 ? (
            <Typography color="text.secondary">You have no pending jar invites right now.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {invites.map((invite) => (
                <Card key={invite.id} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {invite.jarName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Invited by {invite.invitedByName || invite.invitedByEmail || 'Unknown'} · {invite.role === 'EDITOR' ? 'Editor' : 'Viewer'} access
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(invite.createdAt)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          color="inherit"
                          startIcon={<CloseRoundedIcon />}
                          disabled={inviteActionId === invite.id}
                          onClick={() => handleInviteResponse(invite.id, 'decline')}
                        >
                          Decline
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<CheckRoundedIcon />}
                          disabled={inviteActionId === invite.id}
                          onClick={() => handleInviteResponse(invite.id, 'accept')}
                        >
                          Accept
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Jar Dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setFormErrors({}); }} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Jar</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Jar Name"
              required
              fullWidth
              placeholder="e.g., Wedding, Vacation, Emergency Fund"
              value={newJar.name}
              error={!!formErrors.name}
              helperText={formErrors.name}
              onChange={(e) => {
                setNewJar({ ...newJar, name: e.target.value });
                if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: undefined }));
              }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              placeholder="What is this jar for?"
              value={newJar.description}
              onChange={(e) => setNewJar({ ...newJar, description: e.target.value })}
            />
            <TextField
              label="Target Amount ($)"
              fullWidth
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              value={newJar.targetAmount}
              error={!!formErrors.targetAmount}
              helperText={formErrors.targetAmount || "Optional — set a savings goal"}
              onChange={(e) => {
                setNewJar({ ...newJar, targetAmount: e.target.value });
                if (formErrors.targetAmount) setFormErrors((prev) => ({ ...prev, targetAmount: undefined }));
              }}
            />
            <TextField
              label="Target Date"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={newJar.targetDate}
              onChange={(e) => setNewJar({ ...newJar, targetDate: e.target.value })}
            />

            {/* Icon picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Icon
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ICON_OPTIONS.map((ic) => (
                  <Box
                    key={ic}
                    onClick={() => setNewJar({ ...newJar, icon: ic })}
                    sx={{
                      width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 22, borderRadius: 1, cursor: "pointer",
                      border: newJar.icon === ic ? "2px solid" : "1px solid",
                      borderColor: newJar.icon === ic ? "primary.main" : "divider",
                    }}
                  >
                    {ic}
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Color picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Color
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {COLOR_OPTIONS.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setNewJar({ ...newJar, color: c })}
                    sx={{
                      width: 32, height: 32, borderRadius: "50%", backgroundColor: c, cursor: "pointer",
                      border: newJar.color === c ? "3px solid" : "2px solid transparent",
                      borderColor: newJar.color === c ? "text.primary" : "transparent",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setFormErrors({}); }} disabled={saving}>Cancel</Button>
          <Button onClick={handleAddJar} variant="contained" disabled={saving}>
            {saving ? "Creating…" : "Create Jar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const jar = jars.find((j) => j.id === selectedJarId);
          handleMenuClose();
          if (jar) {
            setSelectedJar(jar);
            setDetailsInitialTab(0);
            setOpenDetailsDialog(true);
          }
        }}>Edit</MenuItem>
        <MenuItem onClick={() => {
          const jar = jars.find((j) => j.id === selectedJarId);
          handleMenuClose();
          if (jar) {
            setSelectedJar(jar);
            setDetailsInitialTab(3);
            setOpenDetailsDialog(true);
          }
        }}>Share</MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setConfirmDeleteId(selectedJarId);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Jar</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this jar? This action cannot be undone and will remove all associated expenses, contributions, and planning items.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (confirmDeleteId) handleDeleteJar(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Jar Details Dialog */}
      <JarDetailsDialog
        open={openDetailsDialog}
        jar={selectedJar}
        onClose={() => setOpenDetailsDialog(false)}
        initialTab={detailsInitialTab}
        onJarUpdated={(updated) => {
          setJars((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
          setSelectedJar(updated);
          void refreshSidebarData();
        }}
      />
    </Box>
  );
}
